import type { JsonPatchOperation } from '@/shared/types/common';

/**
 * 태그 값 타입 (최소 필수 필드)
 */
type TagValue = {
  source?: string;
  tagFQN: string;
  name: string;
  description?: string;
  labelType?: string;
  state?: string;
  [key: string]: unknown;
};

/**
 * 태그 필드 키 타입
 */
type TagFieldKey = 'source' | 'tagFQN' | 'name' | 'description' | 'labelType' | 'state';

/**
 * 태그 기본 필드 맵
 */
const TAG_FIELDS_MAP: Array<{ key: TagFieldKey; path: string }> = [
  { key: 'source', path: 'source' },
  { key: 'tagFQN', path: 'tagFQN' },
  { key: 'name', path: 'name' },
  { key: 'description', path: 'description' },
  { key: 'labelType', path: 'labelType' },
  { key: 'state', path: 'state' },
] as const;

/**
 * 태그/용어 필드의 patch operations 생성
 *
 * @description
 * 태그나 용어의 기본 필드들(source, tagFQN, name, description, labelType, state)에 대한
 * replace operation을 생성합니다.
 *
 * OpenMetadata 방식을 따라 배열 인덱스가 변경될 때
 * 모든 필드를 명시적으로 재선언합니다.
 *
 * @param basePath - operation path의 베이스 경로 (예: `/tags/0`)
 * @param newValue - 새로운 태그/용어 값
 * @returns 생성된 patch operations 배열
 *
 * @example
 * ```typescript
 * const ops = generateTagFieldOperations(
 *   '/tags/0',
 *   {
 *     source: 'Classification',
 *     tagFQN: 'new',
 *     name: 'New',
 *     description: 'new desc',
 *     labelType: 'Manual',
 *     state: 'Confirmed'
 *   }
 * );
 * // Returns: [
 * //   { op: 'replace', path: '/tags/0/source', value: 'Classification' },
 * //   { op: 'replace', path: '/tags/0/tagFQN', value: 'new' },
 * //   { op: 'replace', path: '/tags/0/name', value: 'New' },
 * //   { op: 'replace', path: '/tags/0/description', value: 'new desc' },
 * //   { op: 'replace', path: '/tags/0/labelType', value: 'Manual' },
 * //   { op: 'replace', path: '/tags/0/state', value: 'Confirmed' }
 * // ]
 * ```
 */
export const generateTagFieldOperations = (basePath: string, newValue: TagValue): JsonPatchOperation[] => {
  const operations: JsonPatchOperation[] = [];

  TAG_FIELDS_MAP.forEach(({ key, path }) => {
    if (newValue[key] !== undefined) {
      operations.push({
        op: 'replace',
        path: `${basePath}/${path}`,
        value: newValue[key],
      });
    }
  });

  return operations;
};

/**
 * displayName 필드의 patch operations 생성
 *
 * @description
 * displayName의 추가/수정/삭제 operation을 생성합니다.
 * - 새 값이 있고 기존 값도 있으면: replace
 * - 새 값이 있고 기존 값이 없으면: add
 * - 새 값이 없고 기존 값이 있으면: remove
 *
 * @param basePath - operation path의 베이스 경로
 * @param oldDisplayName - 기존 displayName 값
 * @param newDisplayName - 새로운 displayName 값
 * @returns 생성된 patch operations 배열
 */
export const generateDisplayNameOperations = (
  basePath: string,
  oldDisplayName: string | undefined,
  newDisplayName: string | undefined
): JsonPatchOperation[] => {
  const operations: JsonPatchOperation[] = [];

  if (newDisplayName) {
    operations.push({
      op: oldDisplayName ? 'replace' : 'add',
      path: `${basePath}/displayName`,
      value: newDisplayName,
    });
  } else if (oldDisplayName) {
    operations.push({
      op: 'remove',
      path: `${basePath}/displayName`,
    });
  }

  return operations;
};

/**
 * style 필드의 patch operations 생성
 *
 * @description
 * style 객체의 추가/수정/삭제 operation을 생성합니다.
 * - 새 style이 있고 기존 style도 있으면: replace
 * - 새 style이 있고 기존 style이 없으면: add
 * - 새 style이 없고 기존 style이 있으면: remove
 *
 * @param basePath - operation path의 베이스 경로
 * @param oldStyle - 기존 style 객체
 * @param newStyle - 새로운 style 객체
 * @returns 생성된 patch operations 배열
 */
export const generateStyleOperations = (
  basePath: string,
  oldStyle: Record<string, unknown> | undefined,
  newStyle: Record<string, unknown> | undefined
): JsonPatchOperation[] => {
  const operations: JsonPatchOperation[] = [];

  const hasOldStyle = oldStyle && Object.keys(oldStyle).length > 0;
  const hasNewStyle = newStyle && Object.keys(newStyle).length > 0;

  if (hasNewStyle) {
    operations.push({
      op: hasOldStyle ? 'replace' : 'add',
      path: `${basePath}/style`,
      value: newStyle,
    });
  } else if (hasOldStyle) {
    operations.push({
      op: 'remove',
      path: `${basePath}/style`,
    });
  }

  return operations;
};
